/**
 * Per-user Order History "last viewed" timestamps stored in localStorage.
 * Keyed by userEmail so unread state is independent across users on the same browser.
 */

const STORAGE_PREFIX = 'orderHistoryLastViewed';
const BASELINE_PREFIX = 'orderHistoryUnreadBaseline';

const getUserEmail = (userEmail) =>
    (userEmail || localStorage.getItem('userEmail') || localStorage.getItem('email') || '').trim().toLowerCase();

const getUserKey = (userEmail) => {
    const email = getUserEmail(userEmail);
    return email ? `${STORAGE_PREFIX}:${email}` : null;
};

const getBaselineKey = (userEmail) => {
    const email = getUserEmail(userEmail);
    return email ? `${BASELINE_PREFIX}:${email}` : null;
};

const readMap = (userEmail) => {
    const key = getUserKey(userEmail);
    if (!key) return {};
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeMap = (userEmail, map) => {
    const key = getUserKey(userEmail);
    if (!key) return;
    try {
        localStorage.setItem(key, JSON.stringify(map));
    } catch {
        /* ignore quota / private mode */
    }
};

/**
 * First-time baseline for this user: existing history before this moment is not unread.
 * New history after the baseline counts until the user opens that order's history.
 */
export const getOrCreateUnreadBaseline = (userEmail) => {
    const key = getBaselineKey(userEmail);
    if (!key) return new Date().toISOString();
    try {
        const existing = localStorage.getItem(key);
        if (existing) return existing;
        const now = new Date().toISOString();
        localStorage.setItem(key, now);
        return now;
    } catch {
        return new Date().toISOString();
    }
};

export const getOrderHistoryLastViewedAt = (ordernum, userEmail) => {
    if (!ordernum) return null;
    const map = readMap(userEmail);
    return map[String(ordernum)] || null;
};

export const setOrderHistoryLastViewedAt = (ordernum, isoTimestamp, userEmail) => {
    if (!ordernum) return;
    const map = readMap(userEmail);
    map[String(ordernum)] = isoTimestamp || new Date().toISOString();
    writeMap(userEmail, map);
};

/** Mark this order's history as reviewed for the current user (clears unread). */
export const markOrderHistoryViewed = (ordernum, userEmail) => {
    setOrderHistoryLastViewedAt(ordernum, new Date().toISOString(), userEmail);
};

/**
 * Count history entries newer than lastViewedAt, or newer than the per-user
 * feature baseline when the user has never opened this order's history.
 */
export const countUnreadOrderHistory = (entries, lastViewedAt, baselineAt) => {
    const list = Array.isArray(entries) ? entries : [];
    const cutoffIso = lastViewedAt || baselineAt;
    if (!cutoffIso) return 0;

    const cutoffTs = new Date(cutoffIso).getTime();
    if (Number.isNaN(cutoffTs)) return list.length;

    return list.filter((entry) => {
        const t = new Date(entry?.changedAt).getTime();
        return !Number.isNaN(t) && t > cutoffTs;
    }).length;
};

export const formatUnreadHistoryBadge = (count) => {
    if (!count || count <= 0) return '';
    if (count > 99) return '99+';
    return String(count);
};

export const getUnreadHistoryAriaLabel = (count) => {
    if (!count || count <= 0) return 'View order history';
    const n = count > 99 ? '99+' : count;
    return `${n} unread order history updates`;
};
